import React, { Component } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse } from "reactstrap";
import AdrLaborSetRowV3 from "./AdrLaborSetRowV3";
import { getLaborOption, MAX_COLUMNS } from "./utils/adrUtilsV3";
import { getProjContractorOptions } from "../../_helpers/contractorOptions";
import adrActions from "../../_actions/adrActions";
import { getTradeOption } from "../../_helpers/tradeOptions";
import { ADR_EDIT } from "../../_constants/actionConstants";
import AddLaborCrewModalV3 from "./AddLaborCrewModalV3";
import memoizeOne from "memoize-one";


class AdrLaborSetV3 extends Component {
  state = {
    tradesToDisable: {},
    showAddLaborCrewModal: false,
    sectionIsOpen: true,
  };

  componentDidMount() {
    this.addPlaceholderRowsIfEmpty();

    const tradesToDisable = this.getTradesToDisable();
    this.setState({ tradesToDisable });
  }

  componentDidUpdate(prevProps, prevState) {
    const { laborSetClient } = this.props;

    this.addPlaceholderRowsIfEmpty();

    if (prevProps.laborSetClient !== laborSetClient)
      this.setState({ tradesToDisable: this.getTradesToDisable() });
  }

  addPlaceholderRowsIfEmpty = () => {
    const { dispatch, tabId, laborSetClient } = this.props;
    if (laborSetClient && Object.keys(laborSetClient).length === 0) {
      dispatch({ type: ADR_EDIT.LABORSET.ADD, tabId, rowsToAdd: 4 });
    }
  };

  /**
   * Set trade options to disable when component is mounted and updated.
   * (Note there's no need for laborsToDisable: each AdrLaborSetRowV3 checks
   * tradesToDisable.length === tradeOptions.length to determine whether to disable.)
   */
  getTradesToDisable = () => {
    const { laborSetClient } = this.props;

    let tradesToDisable = {};
    const selectedLaborIds = laborSetClient
      ? new Set(
          Object.values(laborSetClient)
            .filter((lsr) => lsr.laborId)
            .map((lsr) => lsr.laborId)
        )
      : [];

    // For each selected laborId, collect all selected tradeIds
    selectedLaborIds.forEach((labId) => {
      const selectedTradeIds = Object.values(laborSetClient)
        .filter((lsr) => lsr.laborId === labId)
        .map((lsr) => lsr.tradeId);
      tradesToDisable[labId] = selectedTradeIds;
    });

    return tradesToDisable;
  };

  /**
   * Update trade options to disable.
   * @param {Object.<number, { toEnable: number, toDisable: number}>} ttdIn
   */
  updateTradesToDisable = (ttdIn) => {
    const { laborSetClient } = this.props;
    const { tradesToDisable } = this.state;
    let ttdToUpdate = { ...tradesToDisable };

    // Update entries provided thru input param (if one is provided)
    if (ttdIn && typeof ttdIn === "object") {
      // Loop through each incoming labor's tradesToDisable
      for (const lid in ttdIn) {
        // Make a copy of existing tradesToDisable[lid]. If it does not exist (i.e. if
        // trade selector is at null due to no defaultTradeTradeId being available),
        // create as an empty array.
        ttdToUpdate[lid] = tradesToDisable[lid] ? [...tradesToDisable[lid]] : [];

        // First, purge tradeId to enable.
        if (ttdIn[lid].toEnable)
          ttdToUpdate[lid] = ttdToUpdate[lid].filter((t) => t !== ttdIn[lid].toEnable);

        // Then add the newly selected value to disable:
        if (ttdIn[lid].toDisable) ttdToUpdate[lid].push(ttdIn[lid].toDisable);

        // Finally, ensure that each tradesToDisable does not contain duplicates:
        ttdToUpdate[lid] = Array.from(new Set(ttdToUpdate[lid]));
      }
    }

    // Purge any nonexistent laborId keys from tradesToDisable.
    // This happens when rows are deleted or a preexisting labor selection was changed.
    const lsLaborIds = new Set(Object.values(laborSetClient).map((lsr) => lsr.laborId));
    const ttdLaborIds = Object.keys(tradesToDisable).map((lid) => +lid);
    ttdLaborIds.forEach((lid) => {
      if (!lsLaborIds.has(lid)) delete ttdToUpdate[lid];
    });

    this.setState({ tradesToDisable: ttdToUpdate });
  };

  overtimeRowExists = () =>
    Object.values(this.props.laborSetClient).some((lsr) => lsr.showOvertimeRow);

  toggleAddLaborCrewModal = () =>
    this.setState({ showAddLaborCrewModal: !this.state.showAddLaborCrewModal });

  toggleLaborSection = (e) => {
    e.preventDefault();
    this.setState({ sectionIsOpen: !this.state.sectionIsOpen });
  };

  handleClick_addRow = (e) => {
    e.preventDefault();
    const { dispatch, tabId, markAdrDirty } = this.props;
    dispatch({ type: ADR_EDIT.LABORSET.ADD, tabId });
    markAdrDirty();
  };

  handleClick_addLaborCrew = (e) => {
    e.preventDefault();
    this.toggleAddLaborCrewModal();
  };

  showAllOvertimeRows = (e) => {
    e.preventDefault();
    const { dispatch, tabId } = this.props;
    dispatch({ type: ADR_EDIT.LABORSET.SHOW_ALL_OT, tabId });
  };

  hideAllOvertimeRows = (e) => {
    e.preventDefault();
    const { dispatch, tabId } = this.props;
    dispatch({ type: ADR_EDIT.LABORSET.HIDE_ALL_OT, tabId });
  };

  handleDragEnd = (result) => {
    const { dispatch, tabId, laborSetOrder, markAdrDirty } = this.props;
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const newLso = Array.from(laborSetOrder);
    newLso.splice(source.index, 1); // Delete source index
    newLso.splice(destination.index, 0, draggableId); // Insert draggableId at destination index

    dispatch({
      type: ADR_EDIT.LABORSET.REORDER,
      tabId,
      newLaborSetOrder: newLso,
    });
    markAdrDirty();
  };

  renderLaborHoursHeader1 = (wpSortOrder) => {
    const { workPerformedClient: wpc } = this.props;
    let col = 0;
    let lhh1 = []; // laborHoursHeader1

    if (wpSortOrder) {
      for (col = 0; col < wpSortOrder.length; col++) {
        if (wpc[wpSortOrder[col].wpTabId].isIdle) {
          lhh1.push(
            <div key={`th1_labor_hours_${col}`} className="th-labor-hours table-warning">
              <span className="badge badge-danger">Idle</span>
            </div>
          );
        } else {
          lhh1.push(<div key={`th1_labor_hours_${col}`} className="th-labor-hours" />);
        }
      }
    }
    if (col < MAX_COLUMNS) {
      for (let remCol = wpSortOrder.length; remCol < MAX_COLUMNS; remCol++) {
        lhh1.push(<div key={`th1_labor_hours_${remCol}`} className="th-labor-hours" />);
      }
    }

    return lhh1;
  };

  renderLaborHoursHeader2 = (wpSortOrder) => {
    const { workPerformedClient: wpc } = this.props;
    let col = 0;
    let lhh2 = []; // laborHoursHeader2

    for (col = 0; col < wpSortOrder.length; col++) {
      if (wpc[wpSortOrder[col].wpTabId].isIdle) {
        lhh2.push(
          <div
            key={`th2_labor_hours_${col}`}
            className="labor-equip-padding labor-equip-th th-labor-hours table-warning"
          >
            <span>{wpc[wpSortOrder[col].wpTabId].wpShortCaption}</span>
          </div>
        );
      } else {
        lhh2.push(
          <div
            key={`th2_labor_hours_${col}`}
            className="labor-equip-padding labor-equip-th th-labor-hours th-container"
          >
            <span className="th-align-center">
              {wpc[wpSortOrder[col].wpTabId].wpShortCaption}
            </span>
          </div>
        );
      }
    }

    if (col < MAX_COLUMNS) {
      for (
        let remCol = wpSortOrder ? wpSortOrder.length : 0;
        remCol < MAX_COLUMNS;
        remCol++
      ) {
        lhh2.push(<div key={`th2_labor_hours_${remCol}`} className="th-labor-hours" />);
      }
    }

    return lhh2;
  };

  /**
   * Renders labor table header. There are two header rows - first row is mostly
   * blank, used for Idle flag only, and second row displays all the column
   * headers for both static (Contractor, Name, etc.) and dynamic (item or CCO
   * numbers, from wpShortCaption) content.
   * @param {import("../../_typedefs/adrV3TypeDefs").WpSortOrder[]} wpSortOrder - an empty array if null
   */
  renderLaborHeader = (wpSortOrder = []) => {
    const h1 = (
      <div key="th1_labor" className="d-flex flex-row flex-wrap ls-table">
        <div
          key="th1_labor_draghandle"
          className="labor-equip-padding th-labor-draghandle"
        ></div>
        <div
          key="th1_labor_contractor"
          className="labor-equip-padding th-labor-contractor"
        ></div>
        <div key="th1_labor_name" className="labor-equip-padding th-labor-name"></div>
        <div key="th1_labor_trade" className="labor-equip-padding th-labor-trade"></div>
        <div key="th1_labor_add" className="labor-equip-padding th-labor-add"></div>
        <div
          key="th1_labor_time_type"
          className="labor-equip-padding th-labor-time-type"
        ></div>
        {this.renderLaborHoursHeader1(wpSortOrder || [])}
        <div key="th1_labor_dropdown" className="labor-equip-padding td-labor-dropdown" />
        <div key="th1_labor_delete" className="labor-equip-padding td-labor-delete" />
      </div>
    );

    const h2 = (
      <div key="th2_labor" className="d-flex flex-row flex-wrap ls-table">
        <div
          key="th2_labor_draghandle"
          className="labor-equip-padding th-labor-draghandle"
        ></div>
        <div
          key="th2_labor_contractor"
          className="labor-equip-padding labor-equip-th th-labor-contractor"
        >
          <span>Contractor</span>
        </div>
        <div
          key="th2_labor_name"
          className="labor-equip-padding labor-equip-th th-labor-name th-container"
        >
          <span>Name (Default Trade)</span>
        </div>
        <div
          key="th2_labor_trade"
          className="labor-equip-padding labor-equip-th th-labor-trade th-container"
        >
          <span>Trade</span>
        </div>
        <div key="th2_labor_add" className="labor-equip-padding th-labor-add"></div>
        <div
          key="th2_labor_time_type"
          className="labor-equip-padding th-labor-time-type"
        ></div>
        {this.renderLaborHoursHeader2(wpSortOrder || [])}
        <div key="th2_labor_dropdown" className="labor-equip-padding td-labor-dropdown" />
        <div key="th2_labor_delete" className="labor-equip-padding td-labor-delete" />
      </div>
    );

    return [h1, h2];
  };

  memoizedWpSortOrder = memoizeOne((wpc) => {
    const { dispatch, tabId } = this.props;
    return wpc && Object.keys(wpc).length > 0
      ? dispatch(adrActions.getWorkPerformedSortOrder(tabId))
      : null;
  });

  memoizedContractorOptions = memoizeOne((projContractorList) =>
    projContractorList ? getProjContractorOptions(projContractorList).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })) : null
    );

  memoizedLaborOptions = memoizeOne((projLaborList, tradeList) =>
    projLaborList
      ? projLaborList
          .filter((l) => !l.mergedWithLaborId)
          .map((l) => {
            const trade = l.defaultTradeTradeId
              ? tradeList.find((t) => t.tradeId === l.defaultTradeTradeId)
              : null;
            return {
              ...getLaborOption(l, trade ? trade.title : null),
              fullName: l.fullName, // This is to only show the fullName when selected
              contractorId: l.contractorId,
              defaultTradeTradeId: l.defaultTradeTradeId,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      : null
  );

  memoizedTradeOptions = memoizeOne((tradeList) =>
    tradeList ? tradeList.map((t) => getTradeOption(t)).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })) : null
  );

    render() {
    const {
      projectId,
      tabId,
      laborSetClient,
      laborSetOrder,
      workPerformedClient: wpc,
      projContractorList,
      projLaborList,
      tradeList,
      status,
      fetching,
      markAdrDirty,
    } = this.props;
    const { sectionIsOpen, tradesToDisable, showAddLaborCrewModal } = this.state;

    /** @type {WpSortOrder[]} */
    const wpSortOrder = this.memoizedWpSortOrder(wpc);
    /** @type {SelectOption[]} */
    const contractorOptions = this.memoizedContractorOptions(projContractorList);
    /** @type {SelectOption[]} */
    const laborOptions = this.memoizedLaborOptions(projLaborList, tradeList);
    /** @type {SelectOption[]} */
    const tradeOptions = this.memoizedTradeOptions(tradeList);

    return (
      <React.Fragment>
        <div className="card m-2">
          <div className="card-body l-parent">
            <p className="h3">
              <span onClick={this.toggleLaborSection} className="pointer-cursor">
                {sectionIsOpen ? (
                  <FontAwesomeIcon icon="caret-down" className="fa-fw mr-2" />
                ) : (
                  <FontAwesomeIcon icon="caret-right" className="fa-fw mr-2" />
                )}
                Labor
              </span>
            </p>
            <Collapse isOpen={sectionIsOpen}>
              <div className="d-flex flex-column ls-parent">
                {this.renderLaborHeader(wpSortOrder)}
                <DragDropContext onDragEnd={this.handleDragEnd}>
                  <Droppable droppableId="laborSetClient">
                    {(provided) => (
                      <div
                        className="ls-table"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {laborSetOrder &&
                          laborSetOrder.map((rowKey, index) => (
                            <AdrLaborSetRowV3
                              key={rowKey}
                              rowKey={rowKey}
                              rowIndex={index}
                              projectId={projectId}
                              tabId={tabId}
                              wpSortOrder={wpSortOrder}
                              laborSetRow={laborSetClient[rowKey]}
                              contractorOptions={contractorOptions}
                              laborOptions={laborOptions}
                              tradeOptions={tradeOptions}
                              tradesToDisable={tradesToDisable}
                              updateTradesToDisable={this.updateTradesToDisable}
                              status={status}
                              fetching={fetching}
                              markAdrDirty={markAdrDirty}
                            />
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <hr className="my-1"></hr>
              <div className="d-flex flex-row flex-wrap mt-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  id="addLaborWpr"
                  name="addLaborWpr"
                  onClick={this.handleClick_addRow}
                  disabled={status.isReadOnly}
                >
                  <FontAwesomeIcon icon="stream" className="fa-fw mr-2" />
                  Add New Row
                </button>
                <button
                  type="button"
                  id="addLaborCrew"
                  name="addLaborCrew"
                  className="btn btn-secondary ml-2"
                  onClick={this.handleClick_addLaborCrew}
                  disabled={status.isReadOnly}
                >
                  <FontAwesomeIcon icon="users" className="fa-fw mr-2" />
                  Add Crew...
                </button>
                <div className="card ml-4">
                  <div className="card-body p-1 d-flex flex-row">
                    <div className="ml-2 my-auto align-middle">
                      <b>Overtime:</b>
                    </div>
                    <button
                      type="button"
                      id="showAllOtRows"
                      name="showAllOtRows"
                      className="btn btn-sm btn-outline-info ml-2"
                      onClick={this.showAllOvertimeRows}
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      id="hideAllOtRows"
                      name="hideAllOtRows"
                      className="btn btn-sm btn-outline-info ml-2"
                      onClick={this.hideAllOvertimeRows}
                    >
                      {`Show >0 Only`}
                    </button>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        {showAddLaborCrewModal && (
          <AddLaborCrewModalV3
            projectId={projectId}
            tabId={tabId}
            laborOptions={laborOptions}
            updateTradesToDisable={this.updateTradesToDisable}
            showAddLaborCrewModal={showAddLaborCrewModal}
            toggleAddLaborCrewModal={this.toggleAddLaborCrewModal}
          />
        )}
      </React.Fragment>
      );

  }
}

const mapStateToProps = (state, ownProps) => ({
  projContractorList: state.contractors.projContractorLists[ownProps.projectId] || null,
  projLaborList: state.labors.projLaborLists[ownProps.projectId] || null,
  tradeList: state.feederLists.tradeList || null,
  adr: state.adr.openAdrs[ownProps.tabId] || null,
  workPerformedClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].workPerformedClient || null
    : null,
  laborSetClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].laborSetClient || null
    : null,
  laborSetOrder: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].laborSetOrder || []
    : null,
});

export default connect(mapStateToProps)(AdrLaborSetV3);

